import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'

export interface ProtectedRouteProps {
  children: React.ReactNode
  /** Return the current auth token, or null if unauthenticated. */
  getToken: () => string | null
  /** Redirect path when unauthenticated. Defaults to '/login'. */
  loginPath?: string
  /**
   * Optional async check run after confirming a token exists.
   * Returns a block-reason string, or null if access is allowed.
   * Called once per mount — suitable for gating on remote state.
   */
  statusCheck?: () => Promise<string | null>
  /** Rendered when statusCheck returns a non-null reason. */
  BlockedFallback?: React.ComponentType<{ reason: string }>
}

/**
 * Generic route guard. Redirects to loginPath when no token is present.
 * Optionally runs an async statusCheck and renders BlockedFallback on failure.
 */
export default function ProtectedRoute({
  children,
  getToken,
  loginPath = '/login',
  statusCheck,
  BlockedFallback,
}: ProtectedRouteProps) {
  const [blockReason, setBlockReason] = useState<string | null>(null)
  // If no async check is needed, skip the pending state.
  const [checked, setChecked] = useState(!statusCheck)

  useEffect(() => {
    if (!statusCheck) return
    const token = getToken()
    if (!token) {
      setChecked(true)
      return
    }
    statusCheck()
      .then((reason) => {
        if (reason) setBlockReason(reason)
      })
      .catch(() => {
        // Network/unexpected errors — don't block; let child handle it.
      })
      .finally(() => setChecked(true))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!getToken()) return <Navigate to={loginPath} replace />
  if (!checked) return null // Brief loading state — avoids flash.
  if (blockReason && BlockedFallback) return <BlockedFallback reason={blockReason} />
  return <>{children}</>
}
